import { X, Plus, Power, Settings2, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { EnvelopeEditor } from './EnvelopeEditor';
import { useStudioStore } from '@/store/useStudioStore';

interface Effect {
  name: string;
  enabled: boolean;
  preset: string;
  params: { [key: string]: number };
}

export function EffectsRack() {
  const selectedChannelId = useStudioStore((state) => state.selectedChannelId);
  const channels = useStudioStore((state) => state.channels);
  const selectedChannel = channels.findIndex(ch => ch.id === selectedChannelId);
  const [effects, setEffects] = useState<Effect[]>([
    { 
      name: 'Parametric EQ 2', 
      enabled: true, 
      preset: 'Default',
      params: { low: 0, mid: -2, high: 3, freq: 1000 }
    },
    { 
      name: 'Reverb 2', 
      enabled: true, 
      preset: 'Medium Hall',
      params: { size: 65, damping: 45, mix: 30 }
    },
    { 
      name: 'Compressor', 
      enabled: false, 
      preset: 'Soft',
      params: { threshold: -20, ratio: 4, attack: 10, release: 50 }
    },
  ]);

  const [showEnvelope, setShowEnvelope] = useState(true);

  const availableEffects = [
    'Parametric EQ 2',
    'Reverb 2',
    'Delay',
    'Compressor',
    'Distortion',
    'Chorus',
    'Phaser',
    'Filter',
    'Stereo Enhancer',
    'Limiter',
  ];

  return (
    <div className="w-96 bg-[#1f1f1f] border-l border-[#3a3a3a] flex flex-col">
      <div className="h-10 bg-[#252525] flex items-center justify-between px-3 border-b border-[#3a3a3a]">
        <span className="text-xs">EFFECTS - Channel {selectedChannel + 1}</span>
        <button className="hover:bg-[#3a3a3a] p-1 rounded transition-colors">
          <X size={14} />
        </button>
      </div>

      {/* Effects list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {effects.map((effect, index) => (
          <div
            key={index}
            className={`bg-[#252525] rounded p-3 border transition-all ${
              effect.enabled ? 'border-[#ff6b00] shadow-lg shadow-[#ff6b00]/10' : 'border-[#3a3a3a]'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newEffects = [...effects];
                    newEffects[index].enabled = !newEffects[index].enabled;
                    setEffects(newEffects);
                  }}
                  className={`p-1.5 rounded transition-all ${
                    effect.enabled 
                      ? 'bg-green-600 shadow-lg shadow-green-600/50' 
                      : 'bg-[#3a3a3a]'
                  }`}
                >
                  <Power size={12} />
                </button>
                <div>
                  <div className="text-xs">{effect.name}</div>
                  <div className="text-[9px] opacity-50">Slot {index + 1}</div>
                </div>
              </div>
              <button className="opacity-50 hover:opacity-100">
                <Settings2 size={12} />
              </button>
            </div>

            {/* Preset selector */}
            <div className="mb-3">
              <select className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded px-2 py-1.5 text-[10px]">
                <option>{effect.preset}</option>
                <option>Default</option>
                <option>User Preset 1</option>
                <option>User Preset 2</option>
              </select>
            </div>

            {/* Parameters */}
            {effect.enabled && (
              <div className="space-y-3">
                {Object.entries(effect.params).map(([param, value]) => (
                  <div key={param}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] opacity-70 capitalize">{param}</label>
                      <span className="text-[10px] text-[#ff6b00]">{value}</span>
                    </div>
                    <input
                      type="range"
                      min="-24"
                      max="24"
                      value={value}
                      onChange={(e) => {
                        const newEffects = [...effects];
                        newEffects[index].params[param] = Number(e.target.value);
                        setEffects(newEffects);
                      }}
                      className="w-full"
                    />
                  </div>
                ))}
                
                {/* Visual representation */}
                {effect.name.includes('EQ') && (
                  <div className="h-20 bg-[#1a1a1a] rounded p-2">
                    <svg viewBox="0 0 100 40" className="w-full h-full">
                      <path
                        d={`M 0,${20 - effect.params.low} L 33,${20 - effect.params.low} L 50,${20 - effect.params.mid} L 67,${20 - effect.params.high} L 100,${20 - effect.params.high}`}
                        fill="none"
                        stroke="#ff6b00"
                        strokeWidth="2"
                        style={{ filter: 'drop-shadow(0 0 2px #ff6b00)' }}
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add effect button */}
        <div className="relative group">
          <button className="w-full py-3 bg-[#252525] hover:bg-[#2a2a2a] rounded border border-dashed border-[#3a3a3a] flex items-center justify-center gap-2 text-xs opacity-70 hover:opacity-100 transition-all">
            <Plus size={14} />
            Add Effect
          </button>
          
          {/* Dropdown menu */}
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#252525] border border-[#3a3a3a] rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all max-h-48 overflow-y-auto">
            {availableEffects.map((effectName) => (
              <button
                key={effectName}
                className="w-full px-3 py-2 text-xs text-left hover:bg-[#2a2a2a] transition-colors"
                onClick={() => {
                  setEffects([
                    ...effects,
                    {
                      name: effectName,
                      enabled: true,
                      preset: 'Default',
                      params: { param1: 50, param2: 50, param3: 50 }
                    }
                  ]);
                }}
              >
                {effectName}
              </button>
            ))}
          </div>
        </div>

        {/* Envelope section */}
        <div className="border-t border-[#3a3a3a] pt-3 mt-3">
          <button
            onClick={() => setShowEnvelope(!showEnvelope)}
            className="w-full flex items-center justify-between mb-3 text-xs opacity-70 hover:opacity-100"
          >
            <span>ENVELOPE</span>
            <ChevronDown 
              size={12} 
              className={`transition-transform ${showEnvelope ? 'rotate-180' : ''}`}
            />
          </button>
          {showEnvelope && <EnvelopeEditor channelName={`Channel ${selectedChannel + 1}`} />}
        </div>
      </div>

      {/* Routing section */}
      <div className="border-t border-[#3a3a3a] p-3 bg-[#252525]">
        <div className="text-xs mb-3 opacity-70">ROUTING & SENDS</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[10px]">
            <span>Output</span>
            <select className="bg-[#1a1a1a] border border-[#3a3a3a] rounded px-2 py-1 text-[10px]">
              <option>Master</option>
              <option>Track 1</option>
              <option>Track 2</option>
              <option>Track 3</option>
            </select>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span>Sidechain</span>
            <select className="bg-[#1a1a1a] border border-[#3a3a3a] rounded px-2 py-1 text-[10px]">
              <option>None</option>
              <option>Kick</option>
              <option>Bass</option>
              <option>Snare</option>
            </select>
          </div>
          
          {/* Send amounts */}
          <div className="pt-2 border-t border-[#3a3a3a]">
            <div className="text-[10px] opacity-50 mb-2">SEND LEVELS</div>
            {['Reverb Bus', 'Delay Bus'].map((send) => (
              <div key={send} className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px]">{send}</span>
                  <span className="text-[10px] text-[#ff6b00]">-12dB</span>
                </div>
                <input type="range" className="w-full" defaultValue="30" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}